import { Card } from "antd";
import { useRef } from "react";
import classNames from "classnames";
import styles from "./style.module.scss";
import ImageIcon from "assets/icons/imageIcon";
import ClassTag from "../ClassTag";
import EncourageProgress from "views/uikit/ProgressBars/VLEncourageProgress";
import DeleteIcon from "assets/icons/DeleteIcon";
import ResizeIcon from "assets/icons/ResizeIcon";
import useAutosizerScrollStatus from "hooks/useAutosizerScrollStatus";

interface ClassCardProps {
  value: string;
  onDelete?: (value: string) => void;
  progressValue?: number;
  itemCount?: number;
  min: number;
  isSelected?: boolean;
  items?: CardItem[];
  onItemDelete?: (value: string, mediaId: string) => void;
  classId: string;
  showBlinkingCard?: boolean;
}

export interface CardItem {
  id: string;
  imgUri: string;
}

export const MINIMUM_LENGTH = 8;

export default function ClassCard({
  value,
  onDelete,
  itemCount = 8,
  min = 0,
  isSelected = false,
  items,
  progressValue = 0,
  onItemDelete,
  classId,
  showBlinkingCard,
}: ClassCardProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const progressText =
    progressValue === 0
      ? "Select samples to assign here"
      : progressValue > 0 && progressValue < min
        ? "Add at least 5 to move forward"
        : progressValue === min
          ? "Minimum reached! Add more to improve results"
          : "Nice! More samples = better accuracy";

  const displayCount = Math.max(itemCount, MINIMUM_LENGTH);
  const hasMoreThanEight = itemCount > MINIMUM_LENGTH;
  const squares = Array.from({ length: displayCount });

  // Track overflow status for the resizable container
  const { isOverflowing, isAtBottom } = useAutosizerScrollStatus(
    containerRef,
    { current: null },
    {
      shouldObserveInnerRef: false,
      dependencyList: [displayCount, hasMoreThanEight],
    },
  );

  const cardClassName = `${styles.card} ${isSelected ? styles.selected : ""}`;

  const containerClassnames = classNames(styles.squaresContainer, {
    [styles.resizable]: hasMoreThanEight,
    [styles.overflowing]: isOverflowing && !isAtBottom,
  });

  const handleClassnames = classNames(styles.resizeHandle, {
    [styles.atEnd]: isAtBottom,
  });

  // Extracted helper to render inner square content with multiple conditions
  const renderSquareContent = (i: number, showBlink: boolean) => {
    if (items && items[i]) {
      return (
        <div className={styles.imageWrapper}>
          <img
            src={items[i].imgUri}
            alt={`preview-${i}`}
            className={styles.img}
          />
          {onItemDelete && (
            <>
              <div className={styles.deleteOverlayBackground} />
              <div
                className={styles.deleteOverlayIcon}
                onClick={() => onItemDelete(classId, items[i].id)}
              >
                <DeleteIcon variant="new" />
              </div>
            </>
          )}
        </div>
      );
    }
    if (showBlink) {
      return <div className={styles.cardBlink}></div>;
    }
    return null;
  };

  return (
    <Card
      className={cardClassName}
      title={
        <div className={styles.header}>
          <ClassTag value={value} />
          {onDelete && (
            <div onClick={() => onDelete(classId)}>
              <div aria-label="delete" className={styles.deleteButton}>
                <DeleteIcon variant="new" />
              </div>
            </div>
          )}
        </div>
      }
      bordered
    >
      <EncourageProgress
        value={progressValue || 0}
        min={min}
        icon={<ImageIcon />}
        text={progressText}
      />
      <div className={styles.resizableWrapper}>
        <div ref={containerRef} className={containerClassnames}>
          {squares.map((_, i) => {
            const position = i + 1;
            const showBlinkForSquare = showBlinkingCard && min >= position;

            return (
              <div key={i} className={styles.square} data-testid="square">
                {renderSquareContent(i, showBlinkForSquare as boolean)}
              </div>
            );
          })}
        </div>
        {/* Always show resize handle when resizable */}
        {hasMoreThanEight && (
          <div className={handleClassnames}>
            <ResizeIcon />
          </div>
        )}
        {/* Show overflow overlay when content overflows */}
        {hasMoreThanEight && isOverflowing && !isAtBottom && (
          <div className={styles.overflowOverlay}></div>
        )}
      </div>
      <div className={styles.cardFooter}>
        Select samples in order to add them here
      </div>
    </Card>
  );
}
